/**
 * DeductionForecast — Projected TDS and PF for remaining FY months
 *
 * Consumes GET /api/v1/reports/deduction-forecast
 * Response shape:
 *   data.financial_year  — e.g. "2026-27"
 *   data.processed       — array { month, year, gross, tds, pf, employees }
 *   data.forecast        — array { month, year, projected_tds, projected_pf, is_forecast }
 *   data.summary         — { ytd_tds, ytd_pf, projected_annual_tds, projected_annual_pf,
 *                             months_processed, months_remaining }
 *   data.headcount       — { active_employees, avg_gross }
 */

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { TrendingUp, Users, Calendar } from 'lucide-react';
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import axiosInstance from '../../api/axiosConfig';

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTHS = ['','Jan','Feb','Mar','Apr','May','Jun',
                 'Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtINR = (n) =>
    `₹${parseFloat(n || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 0, maximumFractionDigits: 0,
    })}`;

const fmtINRfull = (n) =>
    `₹${parseFloat(n || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;

const monthLabel = (month, year) =>
    `${MONTHS[parseInt(month)] || '?'} ${String(year).slice(-2)}`;

// ════════════════════════════════════════════════════════════════════════════
const DeductionForecast = () => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(null);

    useEffect(() => {
        setLoading(true);
        axiosInstance.get('/reports/deduction-forecast')
            .then(r => setData(r.data.data))
            .catch(e => setError(e.response?.data?.message || 'Failed to load forecast'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="p-4 text-center">
            <Spinner animation="border" variant="primary" size="sm"/>
            <span className="text-muted ms-2 small">Loading forecast…</span>
        </div>
    );

    if (error) return (
        <Alert variant="danger" className="small m-4">{error}</Alert>
    );

    if (!data) return null;

    const { processed = [], forecast = [], summary = {}, headcount = {} } = data;
    const fyLabel = data.financial_year
        ? `FY ${data.financial_year}`
        : 'Current FY';

    // Build combined chart data: actual months + forecast months
    const chartData = [
        ...processed.map(m => ({
            name:       monthLabel(m.month, m.year),
            TDS_Actual: parseFloat(m.tds || 0),
            PF_Actual:  parseFloat(m.pf  || 0),
            type:       'actual',
        })),
        ...forecast.map(m => ({
            name:         monthLabel(m.month, m.year),
            TDS_Forecast: parseFloat(m.projected_tds || 0),
            PF_Forecast:  parseFloat(m.projected_pf  || 0),
            type:         'forecast',
        })),
    ];

    return (
        <div className="p-3 p-md-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h5 className="fw-bold mb-1">
                        <TrendingUp size={20} className="me-2 text-success"/>
                        Next Month Projection
                    </h5>
                    <p className="text-muted small mb-0">
                        {fyLabel} · Based on {summary.months_processed || 0} months of processed payroll
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Badge bg="primary" className="px-3 py-2">
                        {summary.months_processed || 0} months processed
                    </Badge>
                    <Badge bg="light" text="dark" className="px-3 py-2">
                        {summary.months_remaining || 0} months remaining
                    </Badge>
                </div>
            </div>

            {processed.length === 0 ? (
                <Card className="border-0 shadow-sm p-4">
                    <p className="text-muted small mb-0">
                        No active employees with salary data found.
                        Add employees and set their salaries to see the forecast.
                    </p>
                </Card>
            ) : (
                <>
                    {/* Summary KPIs */}
                    <Row className="g-3 mb-4">
                        <Col xs={6} md={3}>
                            <Card className="border-0 shadow-sm p-3 text-center h-100">
                                <div className="text-muted small mb-1">YTD TDS</div>
                                <div className="fw-bold" style={{ fontSize: 20, color: '#1565C0' }}>
                                    {fmtINR(summary.ytd_tds)}
                                </div>
                            </Card>
                        </Col>
                        <Col xs={6} md={3}>
                            <Card className="border-0 shadow-sm p-3 text-center h-100">
                                <div className="text-muted small mb-1">Projected Annual TDS</div>
                                <div className="fw-bold" style={{ fontSize: 20, color: '#1976D2' }}>
                                    {fmtINR(summary.projected_annual_tds)}
                                </div>
                            </Card>
                        </Col>
                        <Col xs={6} md={3}>
                            <Card className="border-0 shadow-sm p-3 text-center h-100">
                                <div className="text-muted small mb-1">YTD PF</div>
                                <div className="fw-bold" style={{ fontSize: 20, color: '#1B5E20' }}>
                                    {fmtINR(summary.ytd_pf)}
                                </div>
                            </Card>
                        </Col>
                        <Col xs={6} md={3}>
                            <Card className="border-0 shadow-sm p-3 text-center h-100">
                                <div className="text-muted small mb-1">Active Employees</div>
                                <div className="fw-bold d-flex align-items-center justify-content-center gap-1"
                                    style={{ fontSize: 20, color: '#4f46e5' }}>
                                    <Users size={18}/>
                                    {headcount.active_employees || 0}
                                </div>
                                <div className="text-muted" style={{ fontSize: 11 }}>
                                    Avg {fmtINR(headcount.avg_gross)}/mo
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Chart */}
                    <Card className="border-0 shadow-sm mb-4 p-4">
                        <div className="fw-bold mb-3">
                            TDS & PF — Actual vs Projected ({fyLabel})
                        </div>
                        <ResponsiveContainer width="100%" height={260}>
                            <ComposedChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
                                <YAxis tickFormatter={v => `₹${(v/1000).toFixed(0)}K`}
                                    tick={{ fontSize: 11 }}/>
                                <Tooltip
                                    formatter={(v, name) => [fmtINRfull(v), name.replace(/_/g,' ')]}/>
                                <Legend/>
                                <Bar dataKey="TDS_Actual"   name="TDS (Actual)"   fill="#1976D2" radius={[3,3,0,0]}/>
                                <Bar dataKey="PF_Actual"    name="PF (Actual)"    fill="#2E7D32" radius={[3,3,0,0]}/>
                                <Bar dataKey="TDS_Forecast" name="TDS (Projected)" fill="#90CAF9" radius={[3,3,0,0]}/>
                                <Bar dataKey="PF_Forecast"  name="PF (Projected)"  fill="#A5D6A7" radius={[3,3,0,0]}/>
                            </ComposedChart>
                        </ResponsiveContainer>
                        <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                            Darker bars = actual processed payroll.
                            Lighter bars = projected based on monthly average.
                        </div>
                    </Card>

                    {/* Month-by-month table */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white fw-bold border-0 pt-3 pb-2">
                            Month-by-Month Deduction Schedule
                        </Card.Header>
                        <div style={{ overflowX: 'auto' }}>
                            <Table hover className="mb-0 small align-middle">
                                <thead className="bg-light">
                                    <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                                        <th className="ps-4">Month</th>
                                        <th className="text-end">Gross Payroll</th>
                                        <th className="text-end">TDS</th>
                                        <th className="text-end">PF</th>
                                        <th className="text-end pe-4">Employees</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Processed months */}
                                    {processed.map((m, i) => (
                                        <tr key={`p-${i}`}>
                                            <td className="ps-4 fw-medium">
                                                {monthLabel(m.month, m.year)}
                                            </td>
                                            <td className="text-end">{fmtINRfull(m.gross)}</td>
                                            <td className="text-end text-danger fw-medium">
                                                {fmtINRfull(m.tds)}
                                            </td>
                                            <td className="text-end text-warning fw-medium">
                                                {fmtINRfull(m.pf)}
                                            </td>
                                            <td className="text-end pe-4">{m.employees}</td>
                                            <td>
                                                <Badge bg="success" style={{ fontSize: 9 }}>
                                                    Processed
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Separator */}
                                    {forecast.length > 0 && processed.length > 0 && (
                                        <tr>
                                            <td colSpan={6} className="ps-4 py-1"
                                                style={{ background: '#fafbff',
                                                    fontSize: 11, color: '#6b7280',
                                                    fontStyle: 'italic' }}>
                                                ↓ Projected months (based on {summary.months_processed}-month average)
                                            </td>
                                        </tr>
                                    )}

                                    {/* Forecast months */}
                                    {forecast.map((m, i) => (
                                        <tr key={`f-${i}`}
                                            style={{ background: '#fafbff', color: '#6b7280' }}>
                                            <td className="ps-4">{monthLabel(m.month, m.year)}</td>
                                            <td className="text-end">—</td>
                                            <td className="text-end">
                                                ~{fmtINRfull(m.projected_tds)}
                                            </td>
                                            <td className="text-end">
                                                ~{fmtINRfull(m.projected_pf)}
                                            </td>
                                            <td className="text-end pe-4">
                                                ~{headcount.active_employees || '—'}
                                            </td>
                                            <td>
                                                <Badge bg="light" text="dark" style={{ fontSize: 9 }}>
                                                    Projected
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Annual total row */}
                                    <tr className="fw-bold border-top"
                                        style={{ background: '#f8fafc' }}>
                                        <td className="ps-4">Full Year Projection</td>
                                        <td className="text-end">—</td>
                                        <td className="text-end text-danger">
                                            {fmtINR(summary.projected_annual_tds)}
                                        </td>
                                        <td className="text-end text-warning">
                                            {fmtINR(summary.projected_annual_pf)}
                                        </td>
                                        <td className="pe-4" colSpan={2}></td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
};

export default DeductionForecast;
